import { toJSONSchema } from "zod/v4";
import { useEffect, useState } from "preact/hooks";
import { api } from "../../src/api.ts";
import { CodeBlock } from "./uiComponents.tsx";
import { primaryColor, t } from "./theme.ts";

type JSONSchemaObj = {
  type?: string;
  properties?: Record<string, JSONSchemaObj>;
  items?: JSONSchemaObj;
  required?: string[];
  description?: string;
  const?: unknown;
};

const typeLabel = (schema: JSONSchemaObj): string => {
  if (schema.const !== undefined) return JSON.stringify(schema.const);
  if (schema.type === "array") {
    return `${typeLabel(schema.items || {})}[]`;
  }
  return schema.type || "unknown";
};

const SchemaFields = (
  { schema, depth = 0 }: { schema: JSONSchemaObj; depth?: number },
) => {
  const th = t();
  if (schema.type !== "object" || !schema.properties) return null;
  const required = new Set(schema.required || []);
  return (
    <div style={{ marginLeft: depth > 0 ? 20 : 0 }}>
      {Object.entries(schema.properties).map(([name, field]) => (
        <div
          key={name}
          style={{
            marginBottom: 8,
            borderLeft: depth > 0 ? `2px solid ${th.borderLight}` : "none",
            paddingLeft: depth > 0 ? 12 : 0,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
            <code
              style={{
                color: th.primary,
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {name}
            </code>
            <span style={{ color: th.textDim, fontSize: 13 }}>
              {typeLabel(field)}
            </span>
            {!required.has(name) && (
              <span
                style={{
                  color: th.textFaint,
                  fontSize: 12,
                  fontStyle: "italic",
                }}
              >
                optional
              </span>
            )}
          </div>
          {field.description && (
            <div style={{ color: th.textMuted, fontSize: 13, marginTop: 2 }}>
              {field.description}
            </div>
          )}
          {field.type === "object" && field.properties && (
            <SchemaFields schema={field} depth={depth + 1} />
          )}
          {field.type === "array" && field.items?.type === "object" &&
            field.items?.properties && (
            <SchemaFields schema={field.items} depth={depth + 1} />
          )}
        </div>
      ))}
    </div>
  );
};

const endpointExample = (
  name: string,
  inputSchema: JSONSchemaObj,
  authRequired: boolean,
) => {
  const payload: Record<string, unknown> = {};
  if (inputSchema.properties) {
    for (const [key, field] of Object.entries(inputSchema.properties)) {
      payload[key] = exampleValue(field);
    }
  }
  const body: Record<string, unknown> = { endpoint: name };
  if (authRequired) body.token = "YOUR_API_TOKEN";
  if (Object.keys(payload).length > 0) body.payload = payload;
  else body.payload = {};
  return `fetch("https://api.view-chat.com", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(${
    JSON.stringify(body, null, 4).split("\n").map((l, i) =>
      i === 0 ? l : "  " + l
    ).join("\n")
  }),
});`;
};

const exampleValue = (field: JSONSchemaObj): unknown => {
  if (field.const !== undefined) return field.const;
  if (field.type === "string") return field.description ? "..." : "...";
  if (field.type === "number") return 0;
  if (field.type === "boolean") return true;
  if (field.type === "array") return [exampleValue(field.items || {})];
  if (field.type === "object" && field.properties) {
    const obj: Record<string, unknown> = {};
    const required = new Set(field.required || []);
    for (const [key, val] of Object.entries(field.properties)) {
      if (required.has(key)) obj[key] = exampleValue(val);
    }
    return obj;
  }
  return "...";
};

const EndpointDoc = ({ name }: { name: string }) => {
  const th = t();
  const def = api[name as keyof typeof api];
  const inputSchema = toJSONSchema(def.input) as JSONSchemaObj;
  const outputSchema = toJSONSchema(def.output) as JSONSchemaObj;

  return (
    <div
      id={name}
      style={{
        background: th.cardBgAlt,
        borderRadius: 12,
        padding: 24,
        marginBottom: 24,
        border: `1px solid ${th.borderLight}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0, color: th.textBright, fontSize: 22 }}>
          {name}
        </h2>
        {def.authRequired && (
          <span
            style={{
              background: th.accentBg,
              color: th.accent,
              fontSize: 12,
              padding: "2px 8px",
              borderRadius: 4,
              fontWeight: 600,
            }}
          >
            Auth Required
          </span>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <h3
          style={{
            color: th.textMuted,
            fontSize: 14,
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          Request Payload
        </h3>
        {inputSchema.properties &&
            Object.keys(inputSchema.properties).length > 0
          ? <SchemaFields schema={inputSchema} />
          : (
            <div
              style={{ color: th.textFaint, fontSize: 13, fontStyle: "italic" }}
            >
              No parameters required
            </div>
          )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <h3
          style={{
            color: th.textMuted,
            fontSize: 14,
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          Response
        </h3>
        <SchemaFields schema={outputSchema} />
      </div>

      <div>
        <h3
          style={{
            color: th.textMuted,
            fontSize: 14,
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          Example
        </h3>
        <CodeBlock
          code={endpointExample(name, inputSchema, def.authRequired)}
        />
      </div>
    </div>
  );
};

export const DocsPage = () => {
  const th = t();
  const [activeEndpoint, setActiveEndpoint] = useState<string | null>(null);

  // Scroll to endpoint when hash changes
  useEffect(() => {
    const hash = globalThis.location.hash.slice(1);
    if (hash && api[hash as keyof typeof api]) {
      setActiveEndpoint(hash);
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: th.pageBg,
        color: th.text,
      }}
    >
      {/* Left Navigation */}
      <nav
        style={{
          width: 260,
          flexShrink: 0,
          background: th.navBg,
          borderRight: `1px solid ${th.border}`,
          position: "fixed",
          left: 0,
          top: 42, // Position below the main header
          bottom: 0,
          overflowY: "auto",
          padding: "24px 0",
        }}
      >
        <div style={{ padding: "0 20px 16px" }}>
          <h2
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: th.textBright,
              margin: 0,
            }}
          >
            API Docs
          </h2>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
            }}
          >
            <span style={{ fontSize: 13, color: th.textDim }}>
              view-chat.com
            </span>
            <span style={{ color: th.textFaint }}>•</span>
            <a
              href="/llms.txt"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                color: th.primary,
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              llms.txt ↗
            </a>
          </div>
        </div>

        <div style={{ padding: "8px 0" }}>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: th.textFaint,
              padding: "8px 20px",
              fontWeight: 600,
            }}
          >
            Endpoints
          </div>
          {Object.keys(api).map((name) => (
            <a
              key={name}
              href={`#${name}`}
              onClick={() => setActiveEndpoint(name)}
              style={{
                display: "block",
                padding: "8px 20px",
                color: activeEndpoint === name ? th.primary : th.textMuted,
                textDecoration: "none",
                fontSize: 14,
                fontWeight: activeEndpoint === name ? 500 : 400,
                background: activeEndpoint === name
                  ? th.primaryBg
                  : "transparent",
                borderLeft: activeEndpoint === name
                  ? `3px solid ${th.primary}`
                  : "3px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              {name}
            </a>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          marginLeft: 260,
          padding: "24px 40px",
          paddingTop: 66, // Extra space for header (42px) + content padding (24px)
          maxWidth: 800,
        }}
      >
        <h1
          style={{
            fontSize: 32,
            marginBottom: 8,
            color: th.textBright,
          }}
        >
          API Documentation
        </h1>
        <div
          style={{
            background: th.cardBgAccent,
            border: `1px solid ${th.borderAccent}`,
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 20,
            fontSize: 14,
            color: th.textMuted,
          }}
        >
          Using an AI coding agent? Read our{" "}
          <a
            href="/llms.txt"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: th.primary,
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            llms.txt
          </a>{" "}
          or install the{" "}
          <a
            href="https://github.com/uriva/view-chat-skill"
            style={{
              color: th.primary,
              textDecoration: "none",
              fontWeight: 600,
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Chat skill
          </a>{" "}
          to give your agent full access to this API.
        </div>
        <p style={{ color: th.textMuted, marginBottom: 8 }}>
          Base URL:{" "}
          <code style={{ color: th.primary }}>https://api.view-chat.com</code>
        </p>
        <p style={{ color: th.textMuted, marginBottom: 24 }}>
          All requests are <code style={{ color: th.primary }}>POST</code>{" "}
          with JSON body:{" "}
          <code style={{ color: th.primary }}>
            {"{ endpoint, token, payload }"}
          </code>
        </p>

        {Object.keys(api).map((name) => <EndpointDoc key={name} name={name} />)}

        <div
          style={{
            marginTop: 32,
            padding: 16,
            background: th.cardBgAlt,
            borderRadius: 8,
            border: `1px solid ${th.borderLight}`,
          }}
        >
          <h3 style={{ color: th.textBright, marginTop: 0, marginBottom: 8 }}>
            Error Handling
          </h3>
          <p style={{ color: th.textMuted, fontSize: 14, margin: 0 }}>
            On error, the API returns HTTP 400 with{" "}
            <code style={{ color: th.primary }}>{'{ error: "message" }'}</code>.
            Common errors include invalid token, missing required fields, and
            invalid input format.
          </p>
        </div>
      </main>
    </div>
  );
};
